import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  searchService,
  mandateInvitationService,
  getAgencyAgents,
  mandateService,
} from "../../services/api";
import { useDropzone } from "react-dropzone";
import { useDebounce } from "../../hooks/useDebounce";
import CustomDateTimePicker from "../../components/common/CustomDateTimePicker";
import CustomDropdown from "../../components/common/CustomDropdown";
import CustomPhoneInput from "../../components/common/CustomPhoneInput";

// Wyscout placeholder image patterns - these are not real player photos
const PLACEHOLDER_IMAGE_PATTERNS = [
  "ndplayer_100x130.png",
  "ndplayer_",
  "/photos/players/public/ndplayer",
];

/**
 * Check if an image URL is a placeholder/dummy image
 */
const isPlaceholderImage = (url) => {
  if (!url) return true;
  const urlLower = url.toLowerCase();
  return PLACEHOLDER_IMAGE_PATTERNS.some((pattern) =>
    urlLower.includes(pattern.toLowerCase())
  );
};

/**
 * Check if a player has a valid profile photo (not a placeholder)
 */
const playerHasValidPhoto = (player) => {
  if (!player) return false;
  const imageUrl = player.image_url || player.image_path || player.player_image;
  return imageUrl && !isPlaceholderImage(imageUrl);
};

const InvitePlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAgencyMode = Boolean(
    location.state?.mode === "agency" ||
      (typeof window !== "undefined" &&
        window.location.pathname.includes("/agency/"))
  );
  const [formData, setFormData] = useState({
    selectedPlayer: location.state?.selectedPlayer || null,
    email: "",
    contractType: "mandate", // 'representation' or 'mandate'
    selectedCountries: [],
    startDate: "",
    mobile: "",
    endDate: "",
    isExclusive: false,
    agreed: false,
    agentAccountId: "",
  });

  // Search and modal states
  const [playerSearchQuery, setPlayerSearchQuery] = useState("");
  const [playerSearchResults, setPlayerSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Countries data
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Agency mode: agents list
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Player photo states
  const [playerHasImage, setPlayerHasImage] = useState(null); // null = not checked, true/false = checked
  const [isCheckingImage, setIsCheckingImage] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Refs for dropdowns
  const countryDropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  
  // Refs for request cancellation and race condition fix
  const searchAbortControllerRef = useRef(null);
  const currentSearchQueryRef = useRef("");
  const requestIdRef = useRef(0); // Track request order to ignore stale responses

  // Debounced search
  const debouncedSearchQuery = useDebounce(playerSearchQuery, 300);

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      setIsLoadingCountries(true);
      try {
        const response = await searchService.getCountries();
        if (response.success) {
          setCountries(response.data);
          setFilteredCountries(response.data);
        }
      } catch (error) {
        console.error("Failed to load countries:", error);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    loadCountries();
  }, []);

  // Load agency agents when in agency mode
  useEffect(() => {
    const loadAgents = async () => {
      if (!isAgencyMode) return;
      setIsLoadingAgents(true);
      try {
        // Use the new dropdown API for cleaner agent selection
        const dropdownRes = await mandateService.getAgencyAgentsDropdown();

        if (dropdownRes.success) {
          const agentOptions = (Array.isArray(dropdownRes.data) ? dropdownRes.data : []).map((agent) => ({
            account_id: agent.account_id || agent.id,
            name: agent.name,
            profile_picture_url: agent.profile_picture_url || agent.image || agent.avatar_url || null,
          })).filter(a => a.account_id && a.name);

          // If dropdown returns empty list, fallback to licensed/unlicensed endpoints
          if (agentOptions.length === 0) {
            const [licensedRes, unlicensedRes] = await Promise.all([
              getAgencyAgents({ type: "licensed" }),
              getAgencyAgents({ type: "unlicensed" }),
            ]);

            const licensed = Array.isArray(
              licensedRes?.data?.results?.data?.licensed
            )
              ? licensedRes.data.results.data.licensed
              : [];
            const unlicensed = Array.isArray(
              unlicensedRes?.data?.results?.data?.unlicensed
            )
              ? unlicensedRes.data.results.data.unlicensed
              : [];

            const combined = [...licensed, ...unlicensed];
            const uniqueByAccount = new Map();
            combined.forEach((a) => {
              if (!a) return;
              const accountId = a.account_id || a.agent_id || a.id;
              if (accountId && !uniqueByAccount.has(accountId)) {
                uniqueByAccount.set(accountId, {
                  account_id: accountId,
                  name: a.name,
                  profile_picture_url: a.profile_picture_url,
                });
              }
            });
            setAgents(Array.from(uniqueByAccount.values()));
          } else {
            setAgents(agentOptions);
          }
        } else {
          // Fallback to old API if needed
          const [licensedRes, unlicensedRes] = await Promise.all([
            getAgencyAgents({ type: "licensed" }),
            getAgencyAgents({ type: "unlicensed" }),
          ]);

          const licensed = Array.isArray(
            licensedRes?.data?.results?.data?.licensed
          )
            ? licensedRes.data.results.data.licensed
            : [];
          const unlicensed = Array.isArray(
            unlicensedRes?.data?.results?.data?.unlicensed
          )
            ? unlicensedRes.data.results.data.unlicensed
            : [];

          const combined = [...licensed, ...unlicensed];
          const uniqueByAccount = new Map();
          combined.forEach((a) => {
            if (!a) return;
            const accountId = a.account_id || a.agent_id || a.id;
            if (accountId && !uniqueByAccount.has(accountId)) {
              uniqueByAccount.set(accountId, {
                account_id: accountId,
                name: a.name,
                profile_picture_url: a.profile_picture_url,
              });
            }
          });
          setAgents(Array.from(uniqueByAccount.values()));
        }
      } catch (e) {
        console.error("Failed to load agents:", e);
        setAgents([]);
      } finally {
        setIsLoadingAgents(false);
      }
    };

    loadAgents();
  }, [isAgencyMode]);

  // Auto-fill email if player data is available from navigation state
  useEffect(() => {
    if (location.state?.selectedPlayer && !formData.email) {
      // Try to get email from navigation state first, then from player data
      const emailFromState = location.state.email || "";
      const playerEmail = location.state.selectedPlayer.email || "";
      const emailToUse = emailFromState || playerEmail;

      if (emailToUse) {
        setFormData((prev) => ({ ...prev, email: emailToUse }));
      }
    }
  }, [location.state?.selectedPlayer, location.state?.email, formData.email]);

  // Search players when query changes or page changes
  useEffect(() => {
    if (debouncedSearchQuery.trim() && showSearchModal) {
      searchPlayers(debouncedSearchQuery, 1);
      setCurrentPage(1);
    } else if (!debouncedSearchQuery.trim()) {
      // Cancel any pending requests
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      setPlayerSearchResults([]);
      setSearchPagination(null);
      setIsSearching(false);
      currentSearchQueryRef.current = "";
      // Reset request ID to ensure clean state
      requestIdRef.current = 0;
    }
  // searchPlayers intentionally omitted from deps to avoid effect retrigger loops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, showSearchModal]);

  // Cleanup: Cancel pending requests when modal closes or component unmounts
  useEffect(() => {
    return () => {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      requestIdRef.current = 0;
    };
  }, []);

  // Cancel requests when modal closes
  useEffect(() => {
    if (!showSearchModal) {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      currentSearchQueryRef.current = "";
      requestIdRef.current = 0;
    }
  }, [showSearchModal]);

  // Filter countries based on search query
  useEffect(() => {
    if (countrySearchQuery.trim() === "") {
      setFilteredCountries(countries);
    } else {
      const filtered = countries.filter((country) =>
        country.name.toLowerCase().includes(countrySearchQuery.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [countrySearchQuery, countries]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
        setCountrySearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function searchPlayers(query, page = 1) {
    // Cancel previous request if it exists
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    // Increment request ID - this ensures we only process the latest request
    requestIdRef.current += 1;
    const thisRequestId = requestIdRef.current;

    // Track the query this request is for (normalized for comparison)
    const requestQuery = (query || "").trim().toLowerCase();
    currentSearchQueryRef.current = requestQuery;

    setIsSearching(true);
    try {
      const params = {
        type: "players",
        query: query,
        per_page: 10,
        page: page,
      };

      const response = await searchService.search(params, abortController.signal);

      // CRITICAL CHECK: Only process if this is still the latest request
      // If requestIdRef has been incremented, a newer request was made, so ignore this response
      if (thisRequestId !== requestIdRef.current) {
        // This response is stale - a newer request was made, ignore it
        return;
      }

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      // Double-check query still matches (extra safety)
      const currentTrackedQuery = (currentSearchQueryRef.current || "").trim().toLowerCase();
      if (requestQuery !== currentTrackedQuery) {
        // Query has changed, ignore this response
        return;
      }

      if (response.success && response.data) {
        // Updated to handle new response structure
        const players = response.data.results?.players || [];

        // Be tolerant with spacing/casing in local fallback filtering.
        // Backend already returns query-filtered players, so never hide
        // backend results just because local string matching is stricter.
        const normalize = (v) =>
          String(v || "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();
        const q = normalize(requestQuery);
        const filtered = q
          ? players.filter((p) => {
              const name = normalize(p.name);
              const shortName = normalize(p.short_name);
              return name.includes(q) || shortName.includes(q);
            })
          : players;

        const resultsToShow = filtered.length > 0 ? filtered : players;

        // Final check: Make sure this is still the latest request
        if (thisRequestId === requestIdRef.current && !abortController.signal.aborted) {
          setPlayerSearchResults(resultsToShow);
          setSearchPagination(response.pagination || null);
        }
      } else {
        // Final check before updating empty state
        if (thisRequestId === requestIdRef.current && !abortController.signal.aborted) {
          setPlayerSearchResults([]);
          setSearchPagination(null);
        }
      }
    } catch (error) {
      // Ignore abort errors (they're expected when canceling requests)
      if (error.name === 'AbortError' || abortController.signal.aborted) {
        return;
      }
      
      // Only process error if this is still the latest request
      if (thisRequestId === requestIdRef.current && !abortController.signal.aborted) {
        console.error("Player search error:", error);
        setPlayerSearchResults([]);
        setSearchPagination(null);
      }
    } finally {
      // Only update loading state if this is still the current request
      if (thisRequestId === requestIdRef.current && !abortController.signal.aborted) {
        setIsSearching(false);
      }
    }
  }

  const handlePageChange = (page) => {
    if (playerSearchQuery.trim()) {
      setCurrentPage(page);
      // Use debounced query for consistency, but also allow page changes with current query
      const queryToUse = debouncedSearchQuery.trim() || playerSearchQuery.trim();
      if (queryToUse) {
        searchPlayers(queryToUse, page);
      }
    }
  };

  const handlePlayerSelect = async (player) => {
    setFormData((prev) => ({ ...prev, selectedPlayer: player }));
    setShowSearchModal(false);
    setPlayerSearchQuery("");
    setPlayerSearchResults([]);
    setSearchPagination(null);
    // Clear player selection errors
    setErrors((prev) => ({ ...prev, selectedPlayer: "" }));
    
    // Reset image states
    setPlayerHasImage(null);
    setUploadedImageUrl(null);
    setImageFile(null);
    setImagePreview(null);
    
    // Check if player has image
    if (player?.wyscout_id) {
      await checkPlayerPhoto(player.wyscout_id);
    }
  };

  const checkPlayerPhoto = async (wyscoutId) => {
    setIsCheckingImage(true);
    try {
      const response = await mandateInvitationService.checkWyscoutPlayerPhoto(wyscoutId);
      if (response.success) {
        setPlayerHasImage(response.has_photo);
        if (response.has_photo && response.photo_url) {
          setUploadedImageUrl(response.photo_url);
        }
      } else {
        setPlayerHasImage(false);
        console.error("Failed to check player photo:", response.error);
      }
    } catch (error) {
      console.error("Error checking player photo:", error);
      setPlayerHasImage(false);
    } finally {
      setIsCheckingImage(false);
    }
  };

  const handleImageDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, or WebP image.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleImageDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const handleUploadImage = async () => {
    if (!imageFile || !formData.selectedPlayer?.wyscout_id) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploadingImage(true);
    try {
      const response = await mandateInvitationService.uploadWyscoutPlayerPhoto(
        formData.selectedPlayer.wyscout_id,
        imageFile
      );

      if (response.success) {
        setUploadedImageUrl(response.photo_url);
        setPlayerHasImage(true);
        setImageFile(null);
        setImagePreview(null);
        toast.success('Player photo uploaded successfully!');
      } else {
        toast.error(response.error || 'Failed to upload photo');
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error('Failed to upload photo. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleEditPlayer = () => {
    setFormData((prev) => ({ ...prev, selectedPlayer: null }));
    setShowSearchModal(true);
    setPlayerSearchQuery("");
    setPlayerSearchResults([]);
    setSearchPagination(null);
    // Reset image states when changing player
    setPlayerHasImage(null);
    setUploadedImageUrl(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleOpenSearch = () => {
    setShowSearchModal(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleCountryDropdownToggle = () => {
    setShowCountryDropdown(!showCountryDropdown);
    if (!showCountryDropdown) {
      setCountrySearchQuery("");
    }
  };

  const handleCountrySelect = (country) => {
    const isSelected = formData.selectedCountries.some(
      (c) => c.id === country.id
    );

    if (isSelected) {
      // Remove country
      setFormData((prev) => ({
        ...prev,
        selectedCountries: prev.selectedCountries.filter(
          (c) => c.id !== country.id
        ),
      }));
    } else {
      // Add country
      setFormData((prev) => ({
        ...prev,
        selectedCountries: [...prev.selectedCountries, country],
      }));
    }

    // Clear country selection errors
    setErrors((prev) => ({ ...prev, selectedCountries: "" }));
  };

  const handleRemoveCountry = (countryId) => {
    setFormData((prev) => ({
      ...prev,
      selectedCountries: prev.selectedCountries.filter(
        (c) => c.id !== countryId
      ),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear field errors
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.selectedPlayer) {
      newErrors.selectedPlayer = "Please select a player";
    }

    // Check if player has image (required before invite)
    if (formData.selectedPlayer && playerHasImage === false && !uploadedImageUrl) {
      newErrors.playerImage = "Player must have a profile photo. Please upload an image first.";
    }

    if (isAgencyMode && !formData.agentAccountId) {
      newErrors.agentAccountId = "Please select an agent";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    // Mobile number is required (react-phone-input-2 returns empty string if invalid)
    if (!formData.mobile || formData.mobile.trim() === "") {
      newErrors.mobile = "Mobile number is required";
    }

    if (!formData.contractType) {
      newErrors.contractType = "Please select a contract type";
    }

    // For mandate type, countries are required
    if (
      formData.contractType === "mandate" &&
      formData.selectedCountries.length === 0
    ) {
      newErrors.selectedCountries =
        "Please select at least one country for representation";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) >= new Date(formData.endDate)
    ) {
      newErrors.endDate = "End date must be after start date";
    }

    if (!formData.agreed) {
      newErrors.agreed = "You must agree to the terms";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        contract_type: formData.contractType,
        country_ids:
          formData.contractType === "mandate"
            ? formData.selectedCountries.map((c) => c.id)
            : [],
        start_date: formData.startDate,
        end_date: formData.endDate,
        is_exclusive: formData.isExclusive,
        email: formData.email,
        mobile: formData.mobile,
        agreed: formData.agreed,
        ...(isAgencyMode && formData.agentAccountId
          ? { agent_account_id: formData.agentAccountId }
          : {}),
      };
      console.log(formData.selectedPlayer);
      const response = await mandateInvitationService.sendMandateInvitation(
        formData.selectedPlayer.wyscout_id,
        payload
      );

      if (response.success) {
        // Store player_id if returned for future operations
        if (response.player_id) {
          console.log("Player ID created/linked:", response.player_id);
        }

        // Show success toast
        toast.success(
          `Representation invitation sent successfully to ${
            formData.selectedPlayer.display_name || formData.selectedPlayer.name
          }!`,
          {
            duration: 5000,
            style: {
              background: "#f0fdf4",
              border: "1px solid #22c55e",
              color: "#15803d",
            },
          }
        );

        // Navigate back
        if (isAgencyMode) {
          navigate("/agency");
        } else {
          navigate("/agent/mysquad");
        }
      } else {
        // Check if error is due to missing photo
        if (response.requires_photo_upload) {
          setErrors({ 
            submit: response.error || "Player must have a profile photo before sending invitation.",
            playerImage: "Please upload a player photo first."
          });
          toast.error("Please upload a player photo before sending the invitation.");
        } else {
          // Show error toast
          toast.error(response.error || "Failed to send representation invitation");
          setErrors({ submit: response.error });
        }
      }
    } catch (error) {
      console.error("Mandate invitation error:", error);
      const errorMessage =
        "Failed to send representation invitation. Please try again.";
      toast.error(errorMessage);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAgreementText = () => {
    if (formData.contractType === "representation") {
      return "By continuing, you agree to represent the player as their agent. This representation contract will be active for the specified time period.";
    } else {
      return `By continuing, you agree to represent the player in the selected countries for the specified time period. This representation is ${
        formData.isExclusive ? "exclusive" : "non-exclusive"
      } and will expire on the selected end date unless renewed.`;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Header */}
      <div className="border-b bg-white">
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center gap-2 py-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-[#202630]">
              Invite Player
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="w-full px-4 py-6 md:px-8 md:py-8">
        <div className="max-w-full md:max-w-[75%] mx-auto">
          <form onSubmit={handleSubmit} className="w-full space-y-8">
            {/* Player Selection */}
            <div className="space-y-3">
              <label className="block text-base font-medium text-black">
                Select player
              </label>

              <div className="border border-[#E9E9E9] rounded-[5px] h-12 flex items-center">
                {formData.selectedPlayer ? (
                  <div className="flex items-center justify-between w-full px-4">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div
                          className="w-8 h-8 rounded-full bg-gray-300 bg-cover bg-center flex items-center justify-center"
                          style={{
                            backgroundImage: playerHasValidPhoto(formData.selectedPlayer)
                              ? `url(${formData.selectedPlayer.image_url || formData.selectedPlayer.image_path})`
                              : "none",
                          }}
                        >
                          {!playerHasValidPhoto(formData.selectedPlayer) && (
                            <svg
                              className="w-4 h-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                          )}
                        </div>
                        {!playerHasValidPhoto(formData.selectedPlayer) && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-amber-400 rounded-full" />
                        )}
                      </div>
                      <span className="text-base text-[#1A212F]">
                        {formData.selectedPlayer.display_name ||
                          formData.selectedPlayer.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleEditPlayer}
                      className="text-secondary hover:text-gray-900"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleOpenSearch}
                    className="w-full h-full px-4 text-left"
                  >
                    <span className="text-base text-[#626262]">
                      Search for a player...
                    </span>
                  </button>
                )}
              </div>

              {errors.selectedPlayer && (
                <p className="text-sm text-red-500">{errors.selectedPlayer}</p>
              )}

              {/* Player Image Check and Upload Section */}
              {formData.selectedPlayer && (
                <div className="mt-4 space-y-3">
                  {isCheckingImage ? (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                      <span>Checking player photo...</span>
                    </div>
                  ) : playerHasImage === false ? (
                    <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <svg
                          className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-yellow-800 mb-1">
                            Player Photo Required
                          </h4>
                          <p className="text-sm text-yellow-700 mb-3">
                            This player does not have a profile photo. You must upload one before sending the invitation.
                          </p>
                          <ul className="text-sm text-yellow-700 mb-3 list-disc pl-5 space-y-1">
                            <li>The uploaded reference photo becomes the person's profile picture, visible immediately.</li>
                            <li>It cannot be edited or deleted by the agent without TransferPitch support.</li>
                            <li>Representation fraud leads to an irrevocable ban from TransferPitch.</li>
                          </ul>
                          
                          {/* Image Upload Area */}
                          {!uploadedImageUrl && (
                            <div className="space-y-3">
                              {imagePreview ? (
                                <div className="relative">
                                  <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-full max-w-xs h-48 object-cover rounded-lg border border-gray-300"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <div
                                  {...getRootProps()}
                                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                                    isDragActive
                                      ? "border-blue-500 bg-blue-50"
                                      : "border-gray-300 hover:border-gray-400"
                                  }`}
                                >
                                  <input {...getInputProps()} />
                                  <svg
                                    className="w-12 h-12 text-gray-400 mx-auto mb-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                  <p className="text-sm text-gray-600 mb-1">
                                    {isDragActive
                                      ? "Drop the image here"
                                      : "Drag & drop an image here, or click to select"}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    JPEG, PNG, or WebP (max 5MB)
                                  </p>
                                </div>
                              )}
                              
                              {imageFile && (
                                <button
                                  type="button"
                                  onClick={handleUploadImage}
                                  disabled={isUploadingImage}
                                  className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                  {isUploadingImage ? (
                                    <>
                                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                      <span>Uploading...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                      </svg>
                                      <span>Upload Photo</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                          
                          {uploadedImageUrl && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-2 text-sm text-green-800">
                                <svg
                                  className="w-5 h-5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>Photo uploaded successfully!</span>
                              </div>
                              <img
                                src={uploadedImageUrl}
                                alt="Uploaded"
                                className="mt-2 w-24 h-24 object-cover rounded-lg border border-green-300"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : playerHasImage === true && uploadedImageUrl ? (
                    <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-green-800 mb-2">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>Player has a profile photo</span>
                      </div>
                      <p className="text-sm text-green-700 mb-2">This is the reference photo used for identity verification.</p>
                      <img
                        src={uploadedImageUrl}
                        alt="Player"
                        className="w-24 h-24 object-cover rounded-lg border border-green-300"
                      />
                    </div>
                  ) : null}
                  
                  {errors.playerImage && (
                    <p className="text-sm text-red-500">{errors.playerImage}</p>
                  )}
                </div>
              )}
            </div>

            {/* Player Search Modal */}
            {showSearchModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-xl font-semibold text-[#202630]">
                      Search Players
                    </h2>
                    <button
                      type="button"
                      onClick={() => setShowSearchModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Search Input */}
                  <div className="p-6 border-b">
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search for a player..."
                        value={playerSearchQuery}
                        onChange={(e) => setPlayerSearchQuery(e.target.value)}
                        className="w-full h-12 pl-12 pr-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <svg
                        className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      {isSearching && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Results */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {isSearching && playerSearchResults.length === 0 ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="text-center">
                          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                          <p className="text-secondary">Searching players...</p>
                        </div>
                      </div>
                    ) : playerSearchResults.length > 0 ? (
                      <div className="space-y-4">
                        {/* Results List */}
                        <div className="space-y-2">
                          {playerSearchResults.map((player) => (
                            <button
                              key={
                                player.wyscout_id ||
                                player.id ||
                                player.sportmonks_id ||
                                player.name
                              }
                              type="button"
                              onClick={() => handlePlayerSelect(player)}
                              className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                            >
                              <div className="flex items-center gap-4">
                                <div className="relative flex-shrink-0">
                                  <div
                                    className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center"
                                    style={{
                                      backgroundImage: playerHasValidPhoto(player)
                                        ? `url(${player.image_url || player.image_path})`
                                        : "none",
                                    }}
                                  >
                                    {/* Show camera icon if no valid photo */}
                                    {!playerHasValidPhoto(player) && (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <svg
                                          className="w-6 h-6 text-gray-400"
                                          fill="none"
                                          stroke="currentColor"
                                          viewBox="0 0 24 24"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={1.5}
                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                          />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                  {/* No photo badge */}
                                  {!playerHasValidPhoto(player) && (
                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                                      <svg
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 9v2m0 4h.01"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <h3 className="text-base font-medium text-[#1A212F] truncate">
                                      {player.display_name || player.name}
                                    </h3>
                                    {player.date_of_birth && (
                                      <span className="text-sm text-secondary flex-shrink-0">
                                        {Math.floor(
                                          (new Date() -
                                            new Date(player.date_of_birth)) /
                                            (365.25 * 24 * 60 * 60 * 1000)
                                        )}{" "}
                                        years
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                                    {/* Position with club logo beside */}
                                    {player.position && (
                                      <span className="flex items-center gap-2 flex-shrink-0">
                                        {(player.club_image || player.club?.image_url || player.club?.logo) && (
                                          <img
                                            src={player.club_image || player.club?.image_url || player.club?.logo}
                                            alt={player.club?.name || "Club"}
                                            className="w-5 h-5 object-contain rounded-full"
                                            onError={(e) => {
                                              e.target.style.display = "none";
                                            }}
                                          />
                                        )}
                                        <span>{player.position}</span>
                                      </span>
                                    )}

                                    {/* Club name */}
                                    {player.club?.name && (
                                      <span className="truncate max-w-[180px]">
                                        {player.club.name}
                                      </span>
                                    )}

                                    {/* Competition/Division name */}
                                    {(player.division?.name || player.current_competition?.name || player.competition?.name) && (
                                      <span className="truncate max-w-[180px]">
                                        {player.division?.name || player.current_competition?.name || player.competition?.name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>

                        {/* Pagination */}
                        {searchPagination && (
                          <div className="pt-4 border-t">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-secondary">
                                Showing {playerSearchResults.length} of{" "}
                                {searchPagination.count || 0} results
                                {searchPagination.total_pages > 1 && (
                                  <span className="ml-2">
                                    (Page {currentPage} of{" "}
                                    {searchPagination.total_pages})
                                  </span>
                                )}
                              </div>

                              {(searchPagination.total_pages > 1 ||
                                searchPagination.has_more) && (
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handlePageChange(currentPage - 1)
                                    }
                                    disabled={currentPage <= 1}
                                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                  >
                                    Previous
                                  </button>

                                  <span className="px-2 text-sm text-secondary">
                                    {currentPage}
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      handlePageChange(currentPage + 1)
                                    }
                                    disabled={
                                      !searchPagination.has_more &&
                                      currentPage >=
                                        searchPagination.total_pages
                                    }
                                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : playerSearchQuery.trim() && !isSearching ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="text-center">
                          <svg
                            className="w-12 h-12 text-gray-300 mx-auto mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                          <p className="text-secondary">
                            No players found for "{playerSearchQuery}"
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            Try adjusting your search terms
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32">
                        <div className="text-center">
                          <svg
                            className="w-12 h-12 text-gray-300 mx-auto mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                          <p className="text-secondary">
                            Start typing to search for players
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-3">
              <label className="block text-base font-medium text-black">
                Email address
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter player's email address"
                className="w-full h-12 px-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            {/* Mobile Number Field */}
            <div className="space-y-3">
              <CustomPhoneInput
                label="Mobile number"
                value={formData.mobile}
                onChange={(value) => {
                  setFormData((prev) => ({ ...prev, mobile: value }));
                  // Clear mobile errors
                  setErrors((prev) => ({ ...prev, mobile: "" }));
                }}
                placeholder="Enter player's mobile number"
                required
                error={errors.mobile}
                className="!h-12"
              />
            </div>

            {/* Agent Selection - Only for agency mode */}
            {isAgencyMode && (
              <div className="space-y-3">
                <label className="block text-base font-medium text-black">
                  Select agent
                </label>
                <CustomDropdown
                  options={agents.map((a) => ({
                    label: a.name,
                    value: a.account_id,
                    image: a.profile_picture_url || undefined,
                  }))}
                  value={formData.agentAccountId}
                  onChange={(val) =>
                    setFormData((prev) => ({ ...prev, agentAccountId: val }))
                  }
                  placeholder={
                    isLoadingAgents ? "Loading agents..." : "Select an agent"
                  }
                  searchEnabled
                  disabled={isLoadingAgents}
                />
                {errors.agentAccountId && (
                  <p className="text-sm text-red-500">
                    {errors.agentAccountId}
                  </p>
                )}
              </div>
            )}

            {/* Contract Type Selection */}
            <div className="space-y-3">
              <label className="block text-base font-medium text-black">
                Select agreement type for this player
              </label>

              <div className="flex flex-col gap-4 md:flex-row md:gap-10">
                {/* Representation Contract */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="radio"
                      name="contractType"
                      value="representation"
                      checked={formData.contractType === "representation"}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                        formData.contractType === "representation"
                          ? "border-[#374DF5] bg-white"
                          : "border-[#999999] bg-white"
                      }`}
                    >
                      {formData.contractType === "representation" && (
                        <div className="w-5 h-5 rounded-full bg-[#374DF5]" />
                      )}
                    </div>
                  </div>
                  <span className="text-base text-[#0A0F2D]">
                    Representation Contract
                  </span>
                </label>

                {/* Mandate */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="radio"
                      name="contractType"
                      value="mandate"
                      checked={formData.contractType === "mandate"}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                        formData.contractType === "mandate"
                          ? "border-[#374DF5] bg-white"
                          : "border-[#999999] bg-white"
                      }`}
                    >
                      {formData.contractType === "mandate" && (
                        <div className="w-5 h-5 rounded-full bg-[#374DF5]" />
                      )}
                    </div>
                  </div>
                  <span className="text-base text-[#0A0F2D]">
                    Temporary Representation
                  </span>
                </label>
              </div>

              {errors.contractType && (
                <p className="text-sm text-red-500">{errors.contractType}</p>
              )}
            </div>

            {/* Country Selection - Only show for mandate type */}
            {formData.contractType === "mandate" && (
              <div className="space-y-3">
                <label className="block text-base font-medium text-black">
                  Select authorized countries for this player to be represented
                  in
                </label>

                <div className="relative" ref={countryDropdownRef}>
                  <button
                    type="button"
                    onClick={handleCountryDropdownToggle}
                    className="w-full h-12 px-4 border border-[#E9E9E9] rounded-[5px] flex items-center justify-between text-left"
                  >
                    <span
                      className={`text-base ${
                        formData.selectedCountries.length > 0
                          ? "text-[#1A212F]"
                          : "text-[#626262]"
                      }`}
                    >
                      {formData.selectedCountries.length > 0
                        ? `${formData.selectedCountries.length} countries selected`
                        : "Select countries"}
                    </span>
                    <svg
                      className="w-5 h-5 text-[#626262]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Country Dropdown */}
                  {showCountryDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#E9E9E9] rounded-[5px] shadow-lg max-h-64 overflow-hidden">
                      {/* Search Input */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search countries..."
                            value={countrySearchQuery}
                            onChange={(e) =>
                              setCountrySearchQuery(e.target.value)
                            }
                            className="w-full h-8 pl-8 pr-3 border border-[#E9E9E9] rounded text-sm placeholder-[#626262] focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <svg
                            className="w-4 h-4 text-[#626262] absolute left-2 top-1/2 -translate-y-1/2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Countries List */}
                      <div className="max-h-48 overflow-y-auto">
                        {isLoadingCountries ? (
                          <div className="p-4 text-center text-secondary">
                            Loading countries...
                          </div>
                        ) : filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <button
                              key={country.id}
                              type="button"
                              onClick={() => handleCountrySelect(country)}
                              className={`w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0 ${
                                formData.selectedCountries.some(
                                  (c) => c.id === country.id
                                )
                                  ? "bg-blue-50"
                                  : ""
                              }`}
                            >
                              <div
                                className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                                  formData.selectedCountries.some(
                                    (c) => c.id === country.id
                                  )
                                    ? "border-blue-500 bg-blue-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {formData.selectedCountries.some(
                                  (c) => c.id === country.id
                                ) && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </div>

                              {/* Country Flag */}
                              {country.image_url && (
                                <img
                                  src={country.image_url}
                                  alt={country.name}
                                  className="w-5 h-4 object-cover rounded-sm flex-shrink-0"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}

                              <span className="text-sm flex-1">
                                {country.name}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500">
                            No countries found for "{countrySearchQuery}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Selected Countries Chips */}
                {formData.selectedCountries.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.selectedCountries.slice(0, 5).map((country) => (
                      <div
                        key={country.id}
                        className="flex items-center gap-2 bg-[#E9E9E9] rounded px-2 py-1"
                      >
                        {(country.image_url || country.image_path) && (
                          <img
                            src={country.image_url || country.image_path}
                            alt={country.name}
                            className="w-4 h-3 object-cover rounded-sm"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <span className="text-sm text-[#0A0F2D]">
                          {country.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCountry(country.id)}
                          className="text-[#1E1E1E] hover:text-red-500"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                    {formData.selectedCountries.length > 5 && (
                      <div className="bg-[#E9E9E9] rounded px-2 py-1">
                        <span className="text-sm text-[#0A0F2D]">
                          +{formData.selectedCountries.length - 5} more
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {errors.selectedCountries && (
                  <p className="text-sm text-red-500">
                    {errors.selectedCountries}
                  </p>
                )}
              </div>
            )}

            {/* Duration Selection */}
            <div className="space-y-3">
              <label className="block text-base font-medium text-black">
                Choose Representation Duration
              </label>

              <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3">
                <div className="w-full md:flex-1">
                  <CustomDateTimePicker
                    value={
                      formData.startDate ? new Date(formData.startDate) : null
                    }
                    onChange={(date) => {
                      const formattedDate = date
                        ? date.toISOString().split("T")[0]
                        : "";
                      setFormData((prev) => ({
                        ...prev,
                        startDate: formattedDate,
                      }));
                        // Clear start date error on change
                        setErrors((prev) => ({ ...prev, startDate: "" }));
                    }}
                    placeholder="Start date"
                    error={errors.startDate}
                    minDate={null}
                    className="w-full h-12 px-4 border border-[#E9E9E9] rounded-[5px] text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <span className="text-base text-[#626262] font-medium hidden md:inline">
                  --
                </span>

                <div className="w-full md:flex-1">
                  <CustomDateTimePicker
                    value={formData.endDate ? new Date(formData.endDate) : null}
                    onChange={(date) => {
                      const formattedDate = date
                        ? date.toISOString().split("T")[0]
                        : "";
                      setFormData((prev) => ({
                        ...prev,
                        endDate: formattedDate,
                      }));
                        // Clear end date error on change
                        setErrors((prev) => ({ ...prev, endDate: "" }));
                    }}
                    placeholder="End date"
                    error={errors.endDate}
                    minDate={formData.startDate ? new Date(formData.startDate) : null}
                    className="w-full h-12 px-4 border border-[#E9E9E9] rounded-[5px] text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Exclusivity checkbox - Only show for mandate type */}
            {formData.contractType === "mandate" && (
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="relative mt-1">
                    <input
                      type="checkbox"
                      name="isExclusive"
                      checked={formData.isExclusive}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                        formData.isExclusive
                          ? "border-[#2283FF] bg-[#2283FF]"
                          : "border-[#E9E9E9] bg-white"
                      }`}
                    >
                      {formData.isExclusive && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-base text-[#0A0F2D]">
                    Make this representation exclusive
                  </span>
                </label>
              </div>
            )}

            {/* Agreement Checkbox */}
            <div className="space-y-3">
              <label className="flex items-start gap-5 cursor-pointer">
                <div className="relative mt-1">
                  <input
                    type="checkbox"
                    name="agreed"
                    checked={formData.agreed}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div
                    className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                      formData.agreed
                        ? "border-[#2283FF] bg-[#2283FF]"
                        : "border-[#E9E9E9] bg-white"
                    }`}
                  >
                    {formData.agreed && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-base text-[#0A0F2D] leading-relaxed">
                  {getAgreementText()}
                </span>
              </label>

              {errors.agreed && (
                <p className="text-sm text-red-500">{errors.agreed}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col gap-3 pt-4 md:flex-row md:gap-5">
              <button
                type="submit"
                disabled={isSubmitting || (formData.selectedPlayer && playerHasImage === false && !uploadedImageUrl)}
                className="h-12 w-full md:w-auto px-6 bg-[#2283FF] text-white rounded-[5px] hover:bg-blue-600 text-base font-normal shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting && (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                )}
                {isSubmitting ? "Sending..." : "Send invite"}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
                className="h-12 w-full md:w-auto px-6 border border-[#E6E6E6] text-[#1E1E1E] rounded-[5px] hover:bg-gray-50 text-base font-normal disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvitePlayer;
